import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  approvalAccounts: [],
  approvalAccountTypes: [],
  currentType: 0,
  total: 0,
  page: 1,
  pageSize: 10,
  loading: false,
  error: null,
  successMessage: null,
  // Unassigned users state
  unassignedUsers: [],
  unassignedTotal: 0,
  unassignedPage: 1,
  unassignedPageSize: 10,
  unassignedLoading: false,
};

const approvalAccountsSlice = createSlice({
  name: 'approvalAccounts',
  initialState,
  reducers: {
    // Fetch init data
    fetchInit: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchInitSuccess: (state, action) => {
      state.loading = false;
      state.approvalAccountTypes = action.payload.approvalAccountTypes || [];
      state.error = null;
    },
    fetchInitFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Fetch approval accounts
    fetchApprovalAccounts: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchApprovalAccountsSuccess: (state, action) => {
      state.loading = false;
      state.approvalAccounts = action.payload.data || action.payload;
      state.total = action.payload.meta?.total || 0;
      state.error = null;
    },
    fetchApprovalAccountsFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Delete approval account
    deleteApprovalAccount: (state) => {
      state.loading = true;
      state.error = null;
    },
    deleteApprovalAccountSuccess: (state, action) => {
      state.loading = false;
      state.successMessage = action.payload.message;
      state.error = null;
    },
    deleteApprovalAccountFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Bulk delete
    bulkDeleteApprovalAccounts: (state) => {
      state.loading = true;
      state.error = null;
    },
    bulkDeleteApprovalAccountsSuccess: (state, action) => {
      state.loading = false;
      state.successMessage = action.payload.message;
      state.error = null;
    },
    bulkDeleteApprovalAccountsFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Set current type (tab)
    setCurrentType: (state, action) => {
      state.currentType = action.payload;
      state.page = 1; // Reset to first page when switching tabs
    },

    // Pagination
    setPage: (state, action) => {
      state.page = action.payload;
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload;
      state.page = 1; // Reset to first page
    },

    // Fetch unassigned users
    fetchUnassignedUsers: (state) => {
      state.unassignedLoading = true;
      state.error = null;
    },
    fetchUnassignedUsersSuccess: (state, action) => {
      state.unassignedLoading = false;
      state.unassignedUsers = action.payload.data || action.payload;
      state.unassignedTotal = action.payload.meta?.total || 0;
      state.error = null;
    },
    fetchUnassignedUsersFailure: (state, action) => {
      state.unassignedLoading = false;
      state.error = action.payload;
    },

    // Pagination for unassigned users
    setUnassignedPage: (state, action) => {
      state.unassignedPage = action.payload;
    },
    setUnassignedPageSize: (state, action) => {
      state.unassignedPageSize = action.payload;
      state.unassignedPage = 1; // Reset to first page
    },

    // Assign users
    assignUsers: (state) => {
      state.loading = true;
      state.error = null;
    },
    assignUsersSuccess: (state, action) => {
      state.loading = false;
      state.successMessage = action.payload.message;
      state.error = null;
    },
    assignUsersFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Clear messages
    clearError: (state) => {
      state.error = null;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
  },
});

export const {
  fetchInit,
  fetchInitSuccess,
  fetchInitFailure,
  fetchApprovalAccounts,
  fetchApprovalAccountsSuccess,
  fetchApprovalAccountsFailure,
  deleteApprovalAccount,
  deleteApprovalAccountSuccess,
  deleteApprovalAccountFailure,
  bulkDeleteApprovalAccounts,
  bulkDeleteApprovalAccountsSuccess,
  bulkDeleteApprovalAccountsFailure,
  setCurrentType,
  setPage,
  setPageSize,
  fetchUnassignedUsers,
  fetchUnassignedUsersSuccess,
  fetchUnassignedUsersFailure,
  setUnassignedPage,
  setUnassignedPageSize,
  assignUsers,
  assignUsersSuccess,
  assignUsersFailure,
  clearError,
  clearSuccessMessage,
} = approvalAccountsSlice.actions;

// Selectors
export const selectApprovalAccounts = (state) => state.approvalAccounts?.approvalAccounts || [];
export const selectApprovalAccountTypes = (state) => state.approvalAccounts?.approvalAccountTypes || [];
export const selectCurrentType = (state) => state.approvalAccounts?.currentType || 0;
export const selectApprovalAccountsTotal = (state) => state.approvalAccounts?.total || 0;
export const selectApprovalAccountsPage = (state) => state.approvalAccounts?.page || 1;
export const selectApprovalAccountsPageSize = (state) => state.approvalAccounts?.pageSize || 10;
export const selectApprovalAccountsLoading = (state) => state.approvalAccounts?.loading || false;
export const selectApprovalAccountsError = (state) => state.approvalAccounts?.error;
export const selectApprovalAccountsSuccessMessage = (state) => state.approvalAccounts?.successMessage;
export const selectUnassignedUsers = (state) => state.approvalAccounts?.unassignedUsers || [];
export const selectUnassignedTotal = (state) => state.approvalAccounts?.unassignedTotal || 0;
export const selectUnassignedPage = (state) => state.approvalAccounts?.unassignedPage || 1;
export const selectUnassignedPageSize = (state) => state.approvalAccounts?.unassignedPageSize || 10;
export const selectUnassignedLoading = (state) => state.approvalAccounts?.unassignedLoading || false;

export default approvalAccountsSlice.reducer;
